import { useState, useMemo } from "react";
import { RecurrenceSelector } from "./RecurrenceSelector";
import { EventSubtaskEditor } from "./EventSubtaskEditor";
import { DescriptionFields } from "./DescriptionFields";
import { TagInput } from "./TagInput";
import { BasicFields } from "./BasicFields";
import { TimeInputs } from "./TimeInputs";
import { ImageGallery } from "../../../images/components/ImageGallery";
import { PendingImagePicker } from "../../../images/components/PendingImagePicker";
import { ModalActionButton } from "../../../../ui/molecules/ModalActions";
import { useEventFormState } from "../../hooks/useEventFormState";
import type { AddEventFormProps } from "../../types/AddEventFormProps";
import {
  useAddEventFormSubmit,
} from "./useAddEventFormSubmit";

export function AddEventForm({
  defaultCategory,
  defaultDate,
  defaultStartTime,
  defaultEndTime,
  eventToEdit,
  onAdd,
  onAddEvent,
  onUpdateEvent,
  onClose,
}: AddEventFormProps) {
  const isEditing = eventToEdit !== undefined;
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const state = useEventFormState(
    defaultCategory,
    defaultDate,
    defaultStartTime,
    defaultEndTime,
    eventToEdit,
  );

  const { handleSubmit } = useAddEventFormSubmit({
    isEditing,
    eventToEdit,
    state,
    onAdd,
    onAddEvent,
    onUpdateEvent,
    onClose,
    pendingFiles,
  });

  const isFormValid = state.title.trim().length > 0;

  // Détecter si le formulaire a des changements par rapport aux valeurs initiales
  const hasChanges = useMemo(() => {
    if (!isEditing) return true; // En création, toujours actif
    if (!eventToEdit) return true;

    // Comparer chaque champ avec la valeur originale
    return (
      state.title !== eventToEdit.title ||
      state.category !== eventToEdit.category ||
      state.date !== eventToEdit.date ||
      state.startTime !== eventToEdit.startTime ||
      state.endTime !== eventToEdit.endTime ||
      state.allDay !== eventToEdit.allDay ||
      state.description !== eventToEdit.description ||
      state.notes !== eventToEdit.notes ||
      JSON.stringify(state.tags) !== JSON.stringify(eventToEdit.tags) ||
      JSON.stringify(state.recurrence) !== JSON.stringify(eventToEdit.recurrence) ||
      state.reminderMinutes !== eventToEdit.reminderMinutes ||
      state.reminderTime !== (eventToEdit.reminderTime ?? null) ||
      state.reminderType !== (eventToEdit.reminderType ?? null)
    );
  }, [state, eventToEdit, isEditing]);

  return (
    <form
      onSubmit={handleSubmit}
      className="overflow-hidden overflow-y-auto scrollbar-custom flex flex-col h-full"
    >
      {/* Header avec poignée de drag et bouton fermer */}
      <div className="flex items-center justify-between px-4 py-3 md:px-3 md:py-2 border-b border-border/40 sticky top-0 bg-surface z-10 shrink-0">
        {/* Poignée de drag - seulement cette zone déclenche le drag */}
        <div className="flex items-center gap-2 cursor-grab active:cursor-grabbing select-none hidden md:flex" data-drag-handle="true">
          {/* Icône de drag - 6 points horizontaux */}
          <svg className="w-4 h-4 text-ink-faint/40" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="3" cy="5" r="1.5" />
            <circle cx="3" cy="12" r="1.5" />
            <circle cx="3" cy="19" r="1.5" />
            <circle cx="10" cy="5" r="1.5" />
            <circle cx="10" cy="12" r="1.5" />
            <circle cx="10" cy="19" r="1.5" />
          </svg>
          <span className="text-xs font-semibold text-ink">
            {isEditing ? "Modifier" : "Nouvel événement"}
          </span>
        </div>
        {/* Titre mobile */}
        <span className="md:hidden text-sm font-semibold text-ink">
          {isEditing ? "Modifier" : "Nouvel événement"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-2 md:p-1 rounded-md text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
          aria-label="Fermer"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Title */}
      <div className="px-4 py-3 md:px-3 md:py-3 shrink-0">
        <input
          type="text"
          value={state.title}
          onChange={(e) => state.setTitle(e.target.value)}
          placeholder="Titre de l'événement"
          autoFocus
          autoComplete="off"
          className="w-full text-sm bg-transparent border-none focus:outline-none placeholder:text-ink-faint/60 font-medium"
        />
      </div>

      {/* All fields */}
      <div className="px-4 py-2 md:px-3 md:py-2 flex flex-col gap-2 md:gap-2 overflow-y-auto flex-1">
        <BasicFields
          date={state.date}
          onDateChange={state.setDate}
          category={state.category}
          onCategoryChange={state.setCategory}
          allDay={state.allDay}
          onAllDayChange={state.setAllDay}
        />
        {!state.allDay && (
          <TimeInputs
            startTime={state.startTime}
            onStartTimeChange={state.setStartTime}
            endTime={state.endTime}
            onEndTimeChange={state.setEndTime}
          />
        )}
        <DescriptionFields
          description={state.description}
          onDescriptionChange={state.setDescription}
          notes={state.notes}
          onNotesChange={state.setNotes}
        />
        <TagInput
          tags={state.tags}
          tagInput={state.tagInput}
          onTagInputChange={state.setTagInput}
          onTagsChange={state.setTags}
        />
        <RecurrenceSelector
          recurrence={state.recurrence}
          onRecurrenceChange={state.setRecurrence}
          reminderMinutes={state.reminderMinutes}
          reminderTime={state.reminderTime}
          reminderType={state.reminderType}
          onReminderChange={state.setReminderMinutes}
          onReminderTimeChange={state.setReminderTime}
          onReminderTypeChange={state.setReminderType}
          eventDate={state.date}
        />
        <EventSubtaskEditor
          subtasks={state.eventSubtasks}
          onChange={state.setEventSubtasks}
        />
        {isEditing && eventToEdit && (
          <ImageGallery entityType="event" entityId={eventToEdit.id} hideUploader />
        )}
        <PendingImagePicker files={pendingFiles} onChange={setPendingFiles} />
      </div>

      {/* Actions - Footer standardisé */}
      <div className="px-4 pb-4 md:px-3 md:pb-3 pt-2 flex gap-2 shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 1rem))" }}>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-3 md:py-2 text-sm md:text-xs font-medium text-ink-muted hover:text-ink border border-transparent hover:border-border rounded-xl md:rounded-lg transition-colors min-h-[44px] touch-feedback"
        >
          Annuler
        </button>
        <ModalActionButton
          type="submit"
          variant="primary"
          disabled={!isFormValid || (isEditing && !hasChanges)}
          isLoading={false}
          className="flex-[2] min-h-[44px]"
        >
          {isEditing ? "Enregistrer" : "Créer"}
        </ModalActionButton>
      </div>
    </form>
  );
}
